import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { Briefcase, Users2, Zap, Kanban, StickyNote, MessageSquare, X, Star } from "lucide-react";
import RateModal from "@/components/RateModal";

const STAGES = ["pending", "screening", "shortlisted", "interview", "offered", "hired", "rejected"];

export default function HRDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [filterJob, setFilterJob] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [quickOpen, setQuickOpen] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [rateFor, setRateFor] = useState(null);
  const [cats, setCats] = useState([]);

  const loadJobs = useCallback(async () => {
    try { const { data } = await api.get("/jobs/mine"); setJobs(data); } catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadApps = useCallback(async () => {
    try {
      const params = {};
      if (filterJob) params.job_id = filterJob;
      if (filterStatus) params.status_filter = filterStatus;
      const { data } = await api.get("/hr/candidates", { params });
      setApps(data);
    } catch (e) { toast.error(formatError(e)); }
  }, [filterJob, filterStatus]);
  useEffect(() => { loadJobs(); api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, [loadJobs]);
  useEffect(() => { if (tab === "candidates") loadApps(); }, [tab, loadApps]);

  const featureJob = async (job) => {
    if (job.premium_paid) return toast.info("Already premium");
    const r = await openRazorpayCheckout({ packageId: "premium_job_post", jobId: job.id, prefill: { name: user?.name, email: user?.email, contact: user?.mobile } });
    if (r.ok) { toast.success("Job upgraded to premium"); loadJobs(); }
  };
  const closeJob = async (job) => {
    if (!window.confirm(`Close ${job.title}?`)) return;
    try { await api.patch(`/jobs/${job.id}`, { status: "closed" }); toast.success("Job closed"); loadJobs(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const changeStage = async (a, status) => {
    try { await api.patch(`/applications/${a.id}/stage`, { status }); toast.success(`Moved to ${status}`); loadApps(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="hr-dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">HR Dashboard</h1>
          <p className="mt-1 text-sm font-medium text-sarthi-muted">Manage jobs, track candidates, and post urgent requirements.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="hr-quick-req-btn" onClick={() => setQuickOpen(true)} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white">
            <Zap className="h-4 w-4" /> Quick Requirement
          </button>
          <Link to="/contractor/pipeline" data-testid="hr-pipeline-link" className="sarthi-btn-outline inline-flex items-center gap-2 px-4 py-2 text-sm font-bold">
            <Kanban className="h-4 w-4" /> Hiring Pipeline
          </Link>
        </div>
      </div>

      <div className="mb-4 flex gap-2 border-b border-sarthi-border">
        {[{ k: "jobs", label: "Job Management", icon: Briefcase }, { k: "candidates", label: "Candidate Tracking", icon: Users2 }].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={`hr-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 border-b-4 px-4 py-2 text-sm font-bold ${tab === t.k ? "border-sarthi-primary text-sarthi-primary" : "border-transparent text-sarthi-muted"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "jobs" && (
        <div className="sarthi-card overflow-x-auto" data-testid="hr-jobs-panel">
          <table className="w-full text-left">
            <thead className="border-b-2 border-sarthi-border bg-sarthi-accent/50 text-xs font-bold uppercase text-sarthi-muted">
              <tr><th className="px-3 py-3">Title</th><th className="px-3 py-3">Category</th><th className="px-3 py-3">Location</th><th className="px-3 py-3">Applicants</th><th className="px-3 py-3">Status</th><th className="px-3 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} data-testid={`hr-job-row-${j.id}`} className="border-b border-sarthi-border/60">
                  <td className="px-3 py-3 font-semibold">{j.title}{j.is_urgent && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-800">URGENT</span>}{j.premium_paid && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">PREMIUM</span>}</td>
                  <td className="px-3 py-3">{j.category}</td>
                  <td className="px-3 py-3">{j.location}</td>
                  <td className="px-3 py-3">{j.applicant_count || "—"}</td>
                  <td className="px-3 py-3">{j.status}</td>
                  <td className="px-3 py-3 text-right">
                    <Link to={`/contractor/jobs/${j.id}`} className="mr-2 text-xs font-bold text-sarthi-primary underline" data-testid={`hr-edit-${j.id}`}>Edit</Link>
                    {!j.premium_paid && j.status === "open" && <button data-testid={`hr-feature-${j.id}`} onClick={() => featureJob(j)} className="mr-2 text-xs font-bold text-amber-700 underline">Feature</button>}
                    {j.status === "open" && <button data-testid={`hr-close-${j.id}`} onClick={() => closeJob(j)} className="text-xs font-bold text-red-700 underline">Close</button>}
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sarthi-muted">No jobs yet — <Link to="/post-job" className="font-bold text-sarthi-primary underline">post one</Link></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "candidates" && (
        <div data-testid="hr-candidates-panel">
          <div className="mb-3 flex flex-wrap gap-2">
            <select data-testid="hr-filter-job" value={filterJob} onChange={(e) => setFilterJob(e.target.value)} className="rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-sm font-bold">
              <option value="">All jobs</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <select data-testid="hr-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-xl border-2 border-sarthi-border bg-white px-3 py-2 text-sm font-bold">
              <option value="">All statuses</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sarthi-card overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b-2 border-sarthi-border bg-sarthi-accent/50 text-xs font-bold uppercase text-sarthi-muted"><tr><th className="px-3 py-3">Candidate</th><th className="px-3 py-3">Job</th><th className="px-3 py-3">Skills</th><th className="px-3 py-3">Applied</th><th className="px-3 py-3">Status</th><th className="px-3 py-3 text-right">Actions</th></tr></thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} data-testid={`hr-app-${a.id}`} className="border-b border-sarthi-border/60">
                    <td className="px-3 py-3"><div className="font-semibold">{a.worker_name}</div><div className="text-xs text-sarthi-muted">{a.worker_mobile}</div></td>
                    <td className="px-3 py-3 text-sm">{a.job_title}</td>
                    <td className="px-3 py-3 text-xs">{(a.worker_skills || []).slice(0, 3).join(", ")}</td>
                    <td className="px-3 py-3 text-xs text-sarthi-muted">{(a.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-3">
                      <select data-testid={`hr-stage-${a.id}`} value={a.status || "pending"} onChange={(e) => changeStage(a, e.target.value)} className="rounded-lg border border-sarthi-border px-2 py-1 text-xs font-bold">
                        {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button data-testid={`hr-note-${a.id}`} onClick={() => setNoteFor(a)} className="mr-2 inline-flex items-center gap-1 text-xs font-bold text-sarthi-primary underline"><StickyNote className="h-3 w-3" /> Notes</button>
                      {a.status === "hired" && (
                        <button data-testid={`hr-rate-${a.id}`} onClick={() => setRateFor(a)} className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 underline"><Star className="h-3 w-3" /> Rate</button>
                      )}
                    </td>
                  </tr>
                ))}
                {apps.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sarthi-muted">No applications</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {quickOpen && <QuickRequirementModal cats={cats} onClose={() => setQuickOpen(false)} onDone={() => { setQuickOpen(false); loadJobs(); }} />}
      {noteFor && <NotesDrawer application={noteFor} onClose={() => setNoteFor(null)} />}
      <RateModal application={rateFor} open={!!rateFor} onClose={() => setRateFor(null)} onDone={() => setRateFor(null)} />
    </div>
  );
}

function QuickRequirementModal({ cats, onClose, onDone }) {
  const [f, setF] = useState({ title: "", category: cats[0]?.key || "welder", location: "", positions: 1, salary_min: 800, salary_max: 1200, notes: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    try {
      await api.post("/hr/quick-requirement", { ...f, positions: Number(f.positions), salary_min: Number(f.salary_min), salary_max: Number(f.salary_max) });
      toast.success("Urgent requirement posted — matching workers being notified");
      onDone();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="quick-req-modal">
      <div className="sarthi-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">Quick Requirement</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <p className="mb-3 text-xs text-sarthi-muted">Post an urgent hiring need — no full job description required.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input data-testid="qr-title" placeholder="Requirement title" value={f.title} onChange={set("title")} className="rounded-xl border-2 border-sarthi-border px-3 py-2 sm:col-span-2" />
          <select data-testid="qr-category" value={f.category} onChange={set("category")} className="rounded-xl border-2 border-sarthi-border px-3 py-2">{cats.map((c) => <option key={c.key} value={c.key}>{c.name_en}</option>)}</select>
          <input data-testid="qr-location" placeholder="Location / site" value={f.location} onChange={set("location")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="qr-positions" type="number" placeholder="Positions" value={f.positions} onChange={set("positions")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="qr-salary-min" type="number" placeholder="Salary/day min" value={f.salary_min} onChange={set("salary_min")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="qr-salary-max" type="number" placeholder="Salary/day max" value={f.salary_max} onChange={set("salary_max")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <textarea data-testid="qr-notes" placeholder="Any specific notes (skills, PPE, shift)" value={f.notes} onChange={set("notes")} rows={3} className="rounded-xl border-2 border-sarthi-border px-3 py-2 sm:col-span-2" />
        </div>
        <button data-testid="qr-submit" onClick={submit} className="sarthi-btn-primary mt-4 w-full py-3 text-white">Post Quick Requirement</button>
      </div>
    </div>
  );
}

function NotesDrawer({ application, onClose }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/applications/${application.id}/notes`); setNotes(data); }
    catch (e) { toast.error(formatError(e)); }
  }, [application.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (text.trim().length < 1) return;
    try { await api.post(`/applications/${application.id}/notes`, { text: text.trim() }); setText(""); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="notes-drawer">
      <div className="sarthi-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">Notes — {application.worker_name}</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="mb-3 max-h-64 overflow-y-auto rounded-lg border border-sarthi-border/60 p-3">
          {notes.length === 0 && <div className="py-4 text-center text-sm text-sarthi-muted">No notes yet</div>}
          {notes.map((n) => (
            <div key={n.id} className="mb-3 border-b border-sarthi-border/40 pb-2 text-sm">
              <div className="font-semibold text-sarthi-text">{n.text}</div>
              <div className="mt-1 text-xs text-sarthi-muted">by {n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
          ))}
        </div>
        <textarea data-testid="note-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add an internal note (only visible to HR team)…" className="w-full rounded-xl border-2 border-sarthi-border px-3 py-2" />
        <button data-testid="note-add" onClick={add} className="sarthi-btn-primary mt-2 w-full py-2 text-sm text-white">Add note</button>
      </div>
    </div>
  );
}
